import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchActiveHeaderMessage } from "./headerMessageApi";

const HEADER_MESSAGE_REFRESH_MS = 10 * 60 * 1000;

const isMessageActive = (message) => {
  if (!message?.enabled || !message?.text) {
    return false;
  }

  const now = new Date();
  const startsAt = message.startsAt ? new Date(message.startsAt) : null;
  const expiresAt = message.expiresAt ? new Date(message.expiresAt) : null;

  if (startsAt && startsAt > now) {
    return false;
  }

  if (expiresAt && expiresAt < now) {
    return false;
  }

  return true;
};

function HeaderMessageTicker() {
  const { getAccessTokenSilently, user } = useAuth0();
  const [message, setMessage] = useState(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const viewportRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    let ignore = false;

    const loadMessage = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const data = await fetchActiveHeaderMessage(accessToken);

        if (!ignore) {
          setMessage(data?.message || data || null);
        }
      } catch (error) {
        if (!ignore) {
          setMessage(null);
        }
      }
    };

    loadMessage();
    const intervalId = window.setInterval(
      loadMessage,
      HEADER_MESSAGE_REFRESH_MS,
    );

    return () => {
      ignore = true;
      window.clearInterval(intervalId);
    };
  }, [getAccessTokenSilently, user?.sub]);

  useLayoutEffect(() => {
    const updateOverflow = () => {
      const viewport = viewportRef.current;
      const text = textRef.current;

      if (!viewport || !text) {
        return;
      }

      const overflow = text.scrollWidth > viewport.clientWidth;
      setShouldScroll(overflow);
      text.style.setProperty(
        "--header-message-distance",
        `${Math.max(text.scrollWidth - viewport.clientWidth, 0)}px`,
      );
    };

    updateOverflow();
    window.addEventListener("resize", updateOverflow);

    return () => window.removeEventListener("resize", updateOverflow);
  }, [message?.text]);

  if (!isMessageActive(message)) {
    return null;
  }

  return (
    <div className="header-message-slot" title={message.text}>
      <div className="header-message-viewport" ref={viewportRef}>
        <span
          ref={textRef}
          className={
            shouldScroll
              ? "header-message-text header-message-text--scrolling"
              : "header-message-text"
          }
        >
          {message.text}
        </span>
      </div>
    </div>
  );
}

export default HeaderMessageTicker;
